<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useElementVisibility } from '@vueuse/core';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { RouteLocationNormalized, useRoute } from 'vue-router';

import { allowedToLeaveAReview } from '@/api/reviews';
import GreatestHitsBadge from '@/components/base/GreatestHitsBadge.vue';
import Accordion from '@/components/base/layout/Accordion.vue';
import Breadcrumb from '@/components/base/layout/Breadcrumb.vue';
import Container from '@/components/base/layout/Container.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import CmsContent from '@/components/cms/CmsContent.vue';
import BundledRecommendations from '@/components/pdp/BundledRecommendations.vue';
import BuyBlock from '@/components/pdp/buy-block/BuyBlock.vue';
import PricePresentation from '@/components/pdp/buy-block/PricePresentation.vue';
import CXInfoTable from '@/components/pdp/CXInfoTable.vue';
import GalleryWrapper from '@/components/pdp/GalleryWrapper.vue';
import Ingredients from '@/components/pdp/ingredients/Ingredients.vue';
import MobileMiniPDP from '@/components/pdp/MobileMiniPDP.vue';
import OutOfStock from '@/components/pdp/OutOfStock.vue';
import PDPMeta from '@/components/pdp/PDPMeta.vue';
import PDPSkeleton from '@/components/pdp/PDPSkeleton.vue';
import PDPTitle from '@/components/pdp/PDPTitle.vue';
import ProductDescription from '@/components/pdp/ProductDescription.vue';
import ProductExtraInfo from '@/components/pdp/ProductExtraInfo.vue';
import ProductFacts from '@/components/pdp/ProductFacts.vue';
import ProductMeta from '@/components/pdp/ProductMeta.vue';
import Prop65Warning from '@/components/pdp/Prop65Warning.vue';
import RelatedProductSearches from '@/components/pdp/RelatedProductSearches.vue';
import BriefProductReviews from '@/components/pdp/reviews/BriefProductReviews.vue';
import InlineReview from '@/components/pdp/reviews/InlineReview.vue';
import ProductReviews from '@/components/pdp/reviews/ProductReviews.vue';
import ProductReviewsModal from '@/components/pdp/reviews/ProductReviewsModal.vue';
import ReviewTab from '@/components/pdp/reviews/ReviewTab.vue';
import ShopByCategory from '@/components/pdp/ShopByCategory.vue';
import BundleDiscountBadge from '@/components/product-card/BundleDiscountBadge.vue';
import ProductRecommendations from '@/components/recommendations/ProductRecommendations.vue';
import SlimRecommendations from '@/components/recommendations/SlimRecommendations.vue';
import LeaveAReviewModal from '@/components/reviews/leave-a-review/LeaveAReviewModal.vue';
import ReviewsPopper from '@/components/reviews/ReviewsPopper.vue';
import {
  ExpandedRecommendationsReference,
  useRecommendationsFromConfig,
} from '@/composables/dynamic-yield/recommendationsFromConfig';
import { useCallback } from '@/composables/useCallback';
import { useCms } from '@/composables/useCms';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLoginModal } from '@/composables/useLoginModal';
import { useProductDetail } from '@/composables/useProductDetail';
import { GREATEST_HITS_TAG_NAME } from '@/lib/product/tags';
import { ApprovedReviews, SkuForReview } from '@/lib/reviews';
import routerLinkDelegationMixin from '@/mixins/routerLinkDelegation';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { useCustomer } from '@/stores/customer';
import { useProductStore } from '@/stores/productDetail';
import { ListMetadata } from '@/utils/analytics';
import { sendButtonClicked } from '@/utils/analytics/buttonClicked';
import { sendProductViewedEvent } from '@/utils/analytics/productViewedEvent';
import { isPaidTraffic } from '@/utils/browser';
import { adaptLegacyBoolean } from '@/utils/cms';
import {
  analyticsMetadata,
  decisionId,
  isExpandedRecommendationsReference,
  productSlotsFromExpandedReference,
  recommendations,
  title,
} from '@/utils/dynamic-yield/recommendationsRow';
import { isGift as isGiftUtil, isGiftCertificate as isGiftCertificateUtil } from '@/utils/gift';
import { computeSavings } from '@/utils/money';
import {
  findSmallestEligibleVariant,
  getPrice,
  isPhysical,
  NutsProductVariant,
  PredictedShipping,
  priceForQuantity,
  PurchaseOptions,
  toLegacySku,
} from '@/utils/product';
import { ProductCard } from '@/utils/productCard';
import { reportError } from '@/utils/reportError';

export interface Admin {
  bagSizes: string[];
  /**
   * `{ "warehouse": { "sku": "quantity" } }`
   */
  inventory: Record<string, Record<string, number>>;
}

interface Props {
  admin?: Admin;
  isCTCustomer?: boolean;
  isFavorited?: boolean;
  predictedShipping?: PredictedShipping;
  productKey: string;
  reviews: ApprovedReviews;
  selectedVariantSku?: string;
  snackPackMode?: boolean;
  webstoreHeadTitle?: string;
}
const props = withDefaults(defineProps<Props>(), {
  isCTCustomer: false,
  isFavorited: false,
  snackPackMode: false,
  webstoreHeadTitle: '',
});

const route = useRoute();

const customerStore = useCustomer();
const { flags, loadDyFlags } = useFeatureFlags();
const { handleOpen } = useLoginModal();
const { getActiveSubscriptions, getImpulseUpsellEligible, initSdkClient } = useAutoDelivery();

const productStore = ref(useProductStore(props.productKey));
if (!productStore.value.getProduct.isPending) {
  productStore.value.getProduct.isPending =
    !productStore.value.getProduct.error && !productStore.value.rawProduct;
}

const listMetadata = ref<ListMetadata>();

const skuForReview = ref<SkuForReview>();
const displayLeaveAReviewModal = ref(false);

const allowedToLeaveAReviewCallback = useCallback(async () => {
  try {
    skuForReview.value = await allowedToLeaveAReview(props.productKey);
    displayLeaveAReviewModal.value = true;
  } catch (error) {
    reportError(error);
  }
});

const handleLeaveAReview = useCallback(async () => {
  if (!customerStore.customer)
    handleOpen({ initialStep: 'sign-in', callback: allowedToLeaveAReviewCallback });
  else displayLeaveAReviewModal.value = true;
});

const nutritionFacts = computed(() => productStore.value.nutritionFacts);
const tagsReferences = computed(() => productStore.value.tagsReferences);
const product = computed(() => productStore.value.product!);
const tagNames = computed(() => {
  if (product.value.productDiscountApplied) return ['badge-onsale'];
  return tagsReferences.value?.map((tag) => tag.name) ?? [];
});

const activeDescription = computed(
  () => product.value.testDescriptionHtml || product.value.descriptionHtml,
);

const canDisplayProduct = computed(
  () =>
    !productStore.value.getProduct.error &&
    !productStore.value.getProduct.isPending &&
    !!product.value,
);

const ancestors = computed(() => product.value?.ancestors || []);
const breadcrumbs = computed(() => [{ name: 'Home', url: '/' }, ...ancestors.value]);
const hasReviewOrRating = computed(() => props.reviews.totalReviews || props.reviews.totalRatings);

const breadcrumbsDataPromo = () =>
  breadcrumbs.value
    ?.map((breadcrumb) => `${breadcrumb.name} > `)
    .join('')
    .slice(0, -2);

const galleryDataPromos = {
  click: {
    type: '1',
    creative: 'Click: Other Image',
    name: 'Image Carousel',
  },
  listImages: {
    type: '1',
    creative: 'Preview photo',
    name: 'Image Carousel',
  },
  mainImage: {
    type: '1',
    creative: 'Main photo',
    name: 'Image Carousel',
  },
};

const { additionalCustomAttributes, loadFromConfig, recommendationRows } =
  useRecommendationsFromConfig('[CONFIG] PDP - Rows', route, {
    syncContextWithRoute: true,
  });
const { allBackordered, isCustomizable } = useProductDetail(product);

const showMiniPdp = ref(isPaidTraffic(route) && flags.miniPdp);

const handleShowFullPdp = (buttonName: string) => {
  sendButtonClicked({
    buttonName,
    pageSection: 'Mini PDP',
  });
  showMiniPdp.value = false;
};

function isAboveTheFoldRecommendationRow(
  row: Parameters<typeof isExpandedRecommendationsReference>[0],
): row is ExpandedRecommendationsReference & { aboveTheFold: 'true'; layout: 'slim' } {
  return (
    isExpandedRecommendationsReference(row) &&
    !!adaptLegacyBoolean(row.aboveTheFold) &&
    row.layout === 'slim'
  );
}
const aboveTheFoldRecommendationRow = computed(() =>
  recommendationRows.value.find(isAboveTheFoldRecommendationRow),
);
const belowTheFoldRecommendationRows = computed(() =>
  recommendationRows.value.filter((row) => row !== aboveTheFoldRecommendationRow.value),
);

const isGiftCertificate = computed(() => isGiftCertificateUtil(product.value));
const isGift = computed(() => isGiftUtil(product.value));

const getDefaultSku = (variants?: NutsProductVariant[]) => {
  const experimentSku = flags.ppaDefaultToSmallestVariant
    ? findSmallestEligibleVariant(variants)?.sku
    : undefined;

  return props.selectedVariantSku ?? experimentSku ?? variants?.[0].sku;
};

const selections = reactive<PurchaseOptions>({
  markedAsGift: false,
  quantity: 1,
  sku: getDefaultSku(product.value?.variants) ?? '',
  variation: product.value?.variants[0].id ?? 0,
});

const selectedVariant = computed(() => {
  if (!selections.sku) return product.value.variants[0];

  const variants = product.value?.variants ?? [];

  if (isGiftCertificate.value) {
    return variants.find((v) => v.id === selections.variation) ?? variants[0];
  }

  return variants.find((v) => v.sku === selections.sku) ?? variants[0];
});

const images = computed(() =>
  selectedVariant.value?.images?.length ? selectedVariant.value.images : product.value?.images,
);

const titleImage = computed(
  () => selectedVariant.value?.titleImage || product.value?.titleImage || undefined,
);

const showReviewPopper = computed(
  () => !!customerStore.customer && skuForReview.value?.reviewStatus === 'reviewAllowed',
);

const expandedTabs = reactive({
  details: false,
  ingredients: false,
});

const resetExpandedTabs = () => {
  expandedTabs.details = false;
  expandedTabs.ingredients = false;
};

const sendPDPEvents = () => {
  const variant = selectedVariant.value;
  const basePrice = dollars(priceForQuantity(variant, { quantity: 1 }));
  const price = getPrice(variant, selections.autoDelivery?.key);
  const discount = price.discounted
    ? computeSavings(price.value, price.discounted.value).value
    : undefined;

  sendProductViewedEvent({
    allBackordered: allBackordered.value,
    cost: variant?.pieceCost,
    coupon: price.discounted?.discount.description,
    discount,
    eventLocation: 'pdp',
    indexName: listMetadata.value?.indexName,
    itemListName: listMetadata.value?.list,
    name: product.value.name,
    price: priceForQuantity(variant, selections),
    priceBeforeDiscount: priceForQuantity(variant, { quantity: 1 }),
    productId: props.productKey,
    quantity: selections.quantity,
    reportingCategory: variant.reportingCategory,
    sku: selections.sku,
    unitName: variant?.unitName,
    variantName: variant.variantName?.en,
    variantLabel: variant.variantLabel,
    weight: variant.weight,
  });

  const productTypes = [];
  if (ancestors.value.length && ancestors.value[0].name) {
    productTypes.push(ancestors.value[0].name);
  }
  window.dataLayer.push({
    GDR: {
      pagetype: 'product',
      prodid: [toLegacySku(selections.sku)],
      product_type: productTypes,
      value: basePrice,
    },
  });
};

const handleLeaveAReviewSubmitted = () => {
  if (skuForReview.value) skuForReview.value.reviewStatus = 'alreadyHasReview';
};

onMounted(() => {
  watch(
    () => props.productKey,
    async (key) => {
      try {
        const data = localStorage.getItem('listMetadata');
        listMetadata.value = data ? JSON.parse(data) : undefined;
      } catch {
        listMetadata.value = undefined;
      }
      localStorage.removeItem('listMetadata');

      const newProductStore = useProductStore(key);

      await Promise.all([
        newProductStore.getNutritionFacts.execute(),
        newProductStore.getProduct.execute(),
        newProductStore.getTagReferences.execute(),
      ]);

      const variants = newProductStore.product?.variants;
      const smallestSku = findSmallestEligibleVariant(variants)?.sku;
      if (variants && smallestSku && smallestSku !== variants[0].sku) {
        await loadDyFlags('[A/B Test] PPA Default to Smallest Variant', [
          'ppaDefaultToSmallestVariant',
        ]);
      }

      selections.sku = getDefaultSku(newProductStore.product?.variants) ?? '';
      selections.variation = newProductStore.product?.variants[0].id ?? 0;

      productStore.value = newProductStore;

      sendPDPEvents();

      if (newProductStore.product?.variants.every((v) => v.variantLabel)) {
        loadDyFlags('[A/B Test] PPA Variant Label', ['ppaVariantLabel']);
      }

      if (!isPaidTraffic(route)) {
        showMiniPdp.value = false;
      }

      additionalCustomAttributes.value = {
        productRequiresCustomization: String(isCustomizable.value),
      };
      loadFromConfig();

      resetExpandedTabs();

      skuForReview.value = undefined;
      if (customerStore.customer)
        try {
          skuForReview.value = await allowedToLeaveAReview(props.productKey);
        } catch (error) {
          reportError(error);
        }
    },
    { immediate: true },
  );

  watch(
    () => customerStore.customer,
    async () => {
      const [eligible] = await Promise.all([getImpulseUpsellEligible(), getActiveSubscriptions()]);
      if (eligible) selections.autoDelivery = undefined;
    },
  );

  if (props.isCTCustomer) {
    initSdkClient();
    getImpulseUpsellEligible();
    getActiveSubscriptions();
  }
});

const crossSellSection = ref<HTMLElement>();
const crossSellSectionVisible = useElementVisibility(crossSellSection);
const { content: cmsContent } = useCms('cross-sell-section', {
  contentVisible: () => crossSellSectionVisible.value,
  prefetch: 'setup',
});

const isProductReviewsModalOpen = ref(false);

defineOptions({
  mixins: [routerLinkDelegationMixin],
  async criticalData(_: any, to: RouteLocationNormalized) {
    if (typeof window === 'undefined') {
      const incomingProps = to.matched[0].props.default as {
        isCTCustomer: boolean;
        productKey: string;
        selectedVariantSku?: string;
      };

      const autoDelivery = useAutoDelivery();
      const store = useProductStore(incomingProps.productKey);

      const promises: Promise<any>[] = [
        store.getProduct.execute(),
        store.getNutritionFacts.execute(),
        store.getTagReferences.execute(),
      ];

      if (incomingProps.isCTCustomer) {
        autoDelivery.initSdkClient();
        promises.push(
          autoDelivery.getImpulseUpsellEligible(),
          autoDelivery.getActiveSubscriptions(),
        );
      }

      await Promise.all(promises);

      // Load experiment flag if smallest variant differs from first variant
      const variants = store.product?.variants;
      const smallestSku = findSmallestEligibleVariant(variants)?.sku;
      if (variants && smallestSku && smallestSku !== variants[0].sku) {
        await useFeatureFlags().loadDyFlags('[A/B Test] PPA Default to Smallest Variant', [
          'ppaDefaultToSmallestVariant',
        ]);
      }

      if (isPaidTraffic(to)) {
        await useFeatureFlags().loadDyFlags('[A/B Test] Mini PDP', ['miniPdp']);
      }
    }
  },
});

const getDataPromoAttributes = (name: string) => ({
  'data-promo': '1',
  'data-promo-creative': `Tab: ${name}`,
  'data-promo-name': 'Accordion Tabs',
});
</script>

<template>
  <PDPSkeleton v-if="!(canDisplayProduct && product)" class="animate-pulse" />
  <div v-else class="flex flex-col">
    <PDPMeta
      :image="titleImage"
      :product
      :snackPackMode
      :variant="selectedVariant"
      :webstoreTitle="webstoreHeadTitle"
    />
    <ProductMeta
      :predictedShipping
      :prefixedSku="toLegacySku(selectedVariant.sku)"
      :product
      :reviews
      :variant="selectedVariant || product.variants[0]"
    />
    <div class="mx-auto w-full max-w-screen-2xl">
      <Container v-if="aboveTheFoldRecommendationRow" class="my-4 sm:my-8">
        <SlimRecommendations
          :analyticsList="aboveTheFoldRecommendationRow.analyticsList"
          :analyticsMetadata="aboveTheFoldRecommendationRow.analyticsMetadata"
          :decisionId="decisionId(aboveTheFoldRecommendationRow)"
          :enableAddToCart="aboveTheFoldRecommendationRow.enableAddToCart"
          :link="aboveTheFoldRecommendationRow.link"
          pageSection="Above-the-Fold Recommendations"
          :recommendations="
            productSlotsFromExpandedReference(aboveTheFoldRecommendationRow).map(ProductCard.fromDY)
          "
          :title="title(aboveTheFoldRecommendationRow)"
          trackingEventLocation="PDP"
        />
      </Container>
      <div
        class="flex flex-row gap-8 justify-between items-center pt-4 lg:pt-6 lg:flex-row lg:mx-10"
      >
        <Breadcrumb
          :ancestors="breadcrumbs"
          :dataPromo="{ type: '1', creative: breadcrumbsDataPromo(), name: 'Bread Crumbs' }"
          scrollPaddingClasses="px-4 lg:px-0"
        />
      </div>
      <div class="flex relative flex-col pt-3 pb-10 sm:pt-8 lg:flex-row lg:gap-8 lg:mx-10">
        <div class="px-4 lg:hidden lg:px-0">
          <div class="flex gap-1.5 items-center">
            <GreatestHitsBadge
              v-if="tagNames.includes(GREATEST_HITS_TAG_NAME) && !showMiniPdp"
              animation="shine"
              class="font-semibold"
              popper
              :size="32"
              text
            />
            <BundleDiscountBadge :productKey="product.key" />
          </div>

          <Transition name="fade-slide">
            <PDPTitle v-if="!showMiniPdp" class="mt-2" data-test="product-title">
              {{ product.name }}
            </PDPTitle>
          </Transition>
          <CXInfoTable
            v-if="admin"
            :bagSizes="admin.bagSizes"
            class="my-2"
            :inventory="admin.inventory"
            :productKey
          />
          <Transition name="fade-slide">
            <div v-if="!showMiniPdp" class="flex justify-between items-center mt-1.5">
              <PricePresentation class="lg:hidden" :product :selectedVariant :selections />
              <UnstyledButton
                v-if="reviews.totalRatings || reviews.totalRatings"
                @click="isProductReviewsModalOpen = true"
              >
                <InlineReview
                  data-promo="1"
                  data-promo-name="Reviews"
                  data-promo-creative="Review Summary: Top"
                  :reviews
                />
              </UnstyledButton>
            </div>
          </Transition>
        </div>
        <div
          class="relative block mx-auto mt-4 gallery lg:sticky lg:top-20 lg:mt-0 h-[317px] min-h-[317px]"
          :class="{ 'hidden lg:visible lg:inline gallery--mini': showMiniPdp }"
        >
          <GalleryWrapper
            v-if="selectedVariant"
            :dataPromos="galleryDataPromos"
            :images
            :isGift
            :nutritionFacts
            :productName="product.name"
            :tagsReferences
            :titleImage
            :variant="selectedVariant"
            :tagNames
          />
        </div>
        <div class="w-full shrink-0 lg:w-[432px] xl:w-[554px] px-4 lg:px-0">
          <div class="flex flex-col" data-test="buy-block">
            <div class="hidden lg:visible lg:inline">
              <div class="flex gap-2">
                <GreatestHitsBadge
                  v-if="tagNames.includes(GREATEST_HITS_TAG_NAME)"
                  animation="shine"
                  class="font-semibold"
                  popper
                  :size="32"
                  text
                />
                <BundleDiscountBadge :productKey="product.key" />
              </div>
              <PDPTitle class="mt-2">{{ product.name }}</PDPTitle>
              <CXInfoTable
                v-if="admin"
                :bagSizes="admin.bagSizes"
                class="my-2"
                :inventory="admin.inventory"
                :productKey
              />
              <RouterLink
                v-if="reviews.totalRatings || reviews.totalRatings"
                class="active:no-underline focus:no-underline hover:no-underline"
                :to="{ hash: '#reviews' }"
              >
                <InlineReview
                  class="mt-2"
                  data-promo="1"
                  data-promo-name="Reviews"
                  data-promo-creative="Review Summary: Top"
                  :reviews
                />
              </RouterLink>
            </div>
            <PricePresentation
              class="hidden mt-4 lg:visible lg:flex"
              data-test="product-discount"
              :product
              :selectedVariant
              :selections
            />
            <Transition name="fade-slide">
              <MobileMiniPDP
                v-if="showMiniPdp"
                class="mt-8 lg:hidden"
                :image="images[0]"
                :isGreatestHits="tagNames.includes(GREATEST_HITS_TAG_NAME)"
                :product
                :reviews
                :selectedVariant
                :selections
                @show-full-pdp="handleShowFullPdp"
              />
            </Transition>
            <BuyBlock
              v-model="selections"
              :isFavorited
              :listMetadata
              :predictedShipping
              :product
              :showMiniPdp
              @show-full-pdp="handleShowFullPdp"
            />
            <Transition name="fade-slide">
              <div v-if="!showMiniPdp">
                <OutOfStock
                  v-if="selectedVariant.backordered"
                  :backorderedUntil="selectedVariant.backorderedUntil"
                  class="mt-6"
                  :productKey
                />

                <div class="hidden mt-6 lg:visible lg:block" data-test="product-overview">
                  <Header5 headerTag="h2">Overview</Header5>
                  <ProductFacts
                    class="mt-5"
                    :perServingNutritionFacts="nutritionFacts?.perServing"
                    :tagsReferences
                  />
                  <ProductExtraInfo class="mt-4" :productKey :variant="selectedVariant" />
                </div>

                <Accordion
                  v-bind="getDataPromoAttributes('Product Details')"
                  v-model:expanded="expandedTabs.details"
                  class="mt-4 border-t border-solid border-neutral-200 md:mt-6"
                  defaultExpanded
                  headerTag="h2"
                  id="product-description"
                  data-test="product-description"
                >
                  Product Details
                  <template #content>
                    <div class="lg:hidden">
                      <ProductFacts
                        displaySeeMoreButton
                        isMobile
                        :perServingNutritionFacts="nutritionFacts?.perServing"
                        :tagsReferences
                      />
                      <ProductExtraInfo class="mt-4" :productKey :variant="selectedVariant" />
                    </div>
                    <ProductDescription
                      class="mt-4 mb-2 md:mt-0"
                      :description="activeDescription"
                      :healthTips="product.healthTipsHtml"
                    />
                  </template>
                </Accordion>

                <Accordion
                  v-if="selectedVariant?.ingredients || nutritionFacts"
                  v-bind="getDataPromoAttributes('Ingredients &amp; Nutrition Facts')"
                  v-model:expanded="expandedTabs.ingredients"
                  class="border-t border-solid border-neutral-200"
                  headerTag="h2"
                  id="ingredients"
                  data-test="ingredients"
                >
                  Ingredients &amp; Nutrition Facts
                  <template #content>
                    <div class="pb-2">
                      <Ingredients
                        class="border-t border-solid grow border-neutral-200"
                        :ingredients="selectedVariant?.ingredients"
                        :isPhysical="isPhysical(selectedVariant)"
                        :nutritionFacts
                        :servingsPerContainer="selectedVariant?.servingsPerContainer"
                      />
                    </div>
                  </template>
                </Accordion>

                <div
                  v-if="hasReviewOrRating"
                  v-bind="getDataPromoAttributes('Reviews')"
                  class="border-t border-solid border-neutral-200"
                >
                  <ReviewTab
                    class="lg:hidden"
                    headerTag="h2"
                    :reviews
                    @click="isProductReviewsModalOpen = true"
                  />
                  <RouterLink
                    class="hidden lg:visible lg:block active:no-underline focus:no-underline hover:no-underline"
                    :to="{ hash: '#reviews' }"
                  >
                    <ReviewTab headerTag="h2" :reviews />
                  </RouterLink>
                </div>
                <span class="border-t border-solid border-neutral-200" />
                <Prop65Warning
                  v-if="tagsReferences"
                  class="mt-4 lg:mt-6"
                  :productTags="tagsReferences"
                />
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Recommendations Rows -->
    <div class="bg-stone-100">
      <Container>
        <div
          v-once
          class="dynamic-content"
          id="dy-hook-above-recommendations"
          @click="delegateLinksToRouter"
        />
        <div v-for="(row, i) in belowTheFoldRecommendationRows" :key="i">
          <BundledRecommendations
            v-if="isExpandedRecommendationsReference(row) && row.layout === 'bundle'"
            :analyticsMetadata="row.analyticsMetadata"
            :currentProductKey="product.key"
            :decisionId="decisionId(row)"
            :recommendations="productSlotsFromExpandedReference(row).map(ProductCard.fromDY)"
            :spacingDirection="i === recommendationRows.length - 1 ? 'vertical' : 'top'"
            spacingLevel="xs"
            :selectedVariantSku="selectedVariant.sku"
            :title="title(row)"
          />
          <ProductRecommendations
            v-else
            :analyticsList="row.analyticsList"
            :analyticsMetadata="analyticsMetadata(row)"
            :decisionId="decisionId(row)"
            :enableAddToCart="row.enableAddToCart"
            :link="row.link"
            :list="recommendations(row)"
            pageSection="Product Recommendations"
            :spacingDirection="i === recommendationRows.length - 1 ? 'vertical' : 'top'"
            spacingLevel="xs"
            :title="title(row)"
            trackingEventLocation="PDP"
          />
        </div>
      </Container>
    </div>

    <div class="bg-stone-100">
      <Container>
        <RelatedProductSearches class="my-6" :product />
      </Container>
    </div>

    <div v-if="hasReviewOrRating" class="relative order-last lg:order-none">
      <a id="reviews" class="absolute opacity-0 -translate-y-12" />
      <!-- Reviews - desktop only -->
      <Container class="hidden pt-12 pb-6 lg:visible lg:block" data-test="product-reviews">
        <ProductReviews
          :productKey
          :reviews
          :showReviewPopper
          @review-submitted="handleLeaveAReviewSubmitted"
          @write-a-review="handleLeaveAReview.execute"
        />
      </Container>
      <!-- Reviews brief - mobile only -->
      <Container class="my-6 lg:hidden">
        <BriefProductReviews
          :productKey
          :productName="product.name"
          :productImage="titleImage?.small"
          :reviews
          :selectedVariant
        />
        <div class="flex gap-2 justify-between mt-6 border-t border-solid border-neutral-200">
          <ThemedButton
            class="mt-6 w-full"
            data-promo="1"
            data-promo-name="Reviews"
            data-promo-creative="Listing: Load more reviews"
            theme="white"
            @click="isProductReviewsModalOpen = true"
          >
            Load more reviews
          </ThemedButton>
          <component
            :is="showReviewPopper ? ReviewsPopper : 'div'"
            class="w-full"
            location="pdp"
            v-slot="popperProps"
          >
            <ThemedButton
              class="mt-6 w-full"
              :isLoading="handleLeaveAReview.isPending"
              theme="green"
              @click="
                handleLeaveAReview.execute();
                popperProps?.dismiss();
              "
              data-promo="1"
              data-promo-name="Write a review"
              data-promo-creative="PDP"
            >
              Write a review
            </ThemedButton>
          </component>
        </div>
        <SmallBodyText class="mt-2">
          Showing 2 of {{ reviews.totalReviews }} reviews
        </SmallBodyText>
        <ProductReviewsModal
          v-model="skuForReview"
          :isModalOpen="isProductReviewsModalOpen"
          :productImage="titleImage?.small"
          :productKey
          :productName="product.name"
          :reviews
          :selectedVariant
          @handleClose="isProductReviewsModalOpen = false"
          @review-submitted="handleLeaveAReviewSubmitted"
          @write-a-review="handleLeaveAReview.execute"
        />
      </Container>
    </div>
    <div v-else>
      <a id="reviews" class="absolute opacity-0 -translate-y-12" />
      <Container class="pt-12 pb-6">
        <div class="flex flex-col justify-between md:flex-row">
          <div>
            <Header4 headerTag="h3">Customer Reviews</Header4>
            <LargeBodyText class="mt-2 md:mt-3 text-neutral-600">
              Be the first to write a review!
            </LargeBodyText>
          </div>
          <component
            :is="showReviewPopper ? ReviewsPopper : 'div'"
            location="pdp"
            v-slot="popperProps"
          >
            <ThemedButton
              class="mt-6 w-full md:mt-0"
              :isLoading="handleLeaveAReview.isPending"
              theme="green"
              @click="
                handleLeaveAReview.execute();
                popperProps?.dismiss();
              "
              data-promo="1"
              data-promo-name="Write a review"
              data-promo-creative="PDP"
            >
              <BaseBodyText>Write a review</BaseBodyText>
            </ThemedButton>
          </component>
        </div>
      </Container>
    </div>

    <Container class="w-full">
      <ShopByCategory class="my-6" dySelector="[RECOM] PDP-Shop by Category" :limit="12" />
    </Container>

    <div class="mx-auto w-full max-w-screen-2xl" ref="crossSellSection">
      <CmsContent
        :content="cmsContent"
        :dataPromo="{ creative: 'Cross Sell:', name: 'Merchandising', type: '1' }"
        model="cross-sell-section"
      />
    </div>

    <LeaveAReviewModal
      v-if="skuForReview"
      v-model="skuForReview"
      :isOpen="displayLeaveAReviewModal"
      page="pdp"
      @handle-close="displayLeaveAReviewModal = false"
      @review-submitted="handleLeaveAReviewSubmitted"
    />
  </div>
</template>

<style lang="scss" scoped>
h1,
h2,
h3,
h4,
h5,
h6,
p {
  @apply mb-0;
}

.gallery {
  @apply 2xl:h-[670px] min-[1440px]:h-[574px] xl:h-[470px] sm:h-[374px] w-full max-w-[867px] aspect-[4/3];
  transition: height 0.4s ease-in-out;
  overflow: hidden;
  touch-action: pan-x pinch-zoom;
}

.gallery :deep(section) {
  max-height: 100%;
  overflow: hidden;
}

/* Prevent vertical scrolling in mobile gallery */
.gallery :deep(section > div) {
  overflow-y: hidden !important;
}

.gallery :deep(ul) {
  overflow-y: hidden !important;
}

.gallery :deep(li) {
  overflow: hidden;
}

.gallery :deep(img) {
  max-height: 100%;
  height: auto;
}

.gallery--mini {
  @apply 2xl:h-[435px] min-[1440px]:h-[387px] xl:h-[235px] sm:h-[374px];
}

/* Fade and slide transitions for content appearing/disappearing */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
